import React, { useContext, useState, useEffect } from 'react';
import {
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
	TextField,
	Button,
	Switch,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	TablePagination,
	IconButton,
	Box,
	Dialog,
	DialogTitle,
	DialogContent,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import MainCard from '../berry/ui-component/cards/MainCard.jsx';
import UserContext from '../context/user.jsx';
import api_ama_media from '../api/ama_media.js';

const CONFIG = require('../config.json');
const data_base = CONFIG.api.replace(/\/$/, '');

export default function AmaMediaEntries() {
	const { session } = useContext(UserContext);
	const [items, set_items] = useState([]);
	const [total, set_total] = useState(0);
	const [page, set_page] = useState(0);
	const [limit, set_limit] = useState(20);
	const [search, set_search] = useState('');
	const [search_debounce, set_search_debounce] = useState('');
	const [approved_filter, set_approved_filter] = useState('any');
	const [show_filter, set_show_filter] = useState('any');
	const [sort, set_sort] = useState('timestamp');
	const [order, set_order] = useState('desc');
	const [loading, set_loading] = useState(false);
	const [preview_item, set_preview_item] = useState(null);

	useEffect(() => {
		const t = setTimeout(() => set_search_debounce(search), 300);
		return () => clearTimeout(t);
	}, [search]);

	useEffect(() => {
		if (!session) return;
		set_loading(true);
		api_ama_media
			.list({ session, page, limit, search: search_debounce, sort, order, approved: approved_filter, show: show_filter })
			.then((res) => {
				set_items(res.items || []);
				set_total(res.total || 0);
			})
			.catch((e) => console.error(e))
			.finally(() => set_loading(false));
	}, [session, page, limit, search_debounce, sort, order, approved_filter, show_filter]);

	const handle_toggle = (item, field) => {
		const id = item._id || item.info?.id;
		const value = field === 'approved' ? !item.approved : !item.show;
		api_ama_media
			.update_item({ session, id, [field]: value })
			.then(() => {
				set_items((prev) =>
					prev.map((p) =>
						(p._id && p._id.toString() === id?.toString()) || p.info?.id === id
							? { ...p, [field]: value }
							: p
					)
				);
			})
			.catch((e) => console.error(e));
	};

	const resolve_url = (path) => {
		if (!path) return '';
		if (path.startsWith('http')) return path;
		if (path.startsWith('/')) return data_base + path;
		return data_base + '/' + path;
	};

	const thumb_url = (item) =>
		resolve_url(item.preview ?? '');

	return (
		<>
			<Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
				<TextField
					size="small"
					placeholder="Search caption, username, hashtags..."
					value={search}
					onChange={(e) => set_search(e.target.value)}
					sx={{ minWidth: 280 }}
				/>
				<FormControl size="small" sx={{ minWidth: 130 }}>
					<InputLabel id="approved-filter">Approved</InputLabel>
					<Select
						labelId="approved-filter"
						value={approved_filter}
						label="Approved"
						onChange={(e) => {
							set_approved_filter(e.target.value);
							set_page(0);
						}}
					>
						<MenuItem value="any">Any</MenuItem>
						<MenuItem value="approved">Approved</MenuItem>
						<MenuItem value="declined">Declined</MenuItem>
					</Select>
				</FormControl>
				<FormControl size="small" sx={{ minWidth: 110 }}>
					<InputLabel id="show-filter">Show</InputLabel>
					<Select
						labelId="show-filter"
						value={show_filter}
						label="Show"
						onChange={(e) => {
							set_show_filter(e.target.value);
							set_page(0);
						}}
					>
						<MenuItem value="any">Any</MenuItem>
						<MenuItem value="show">Show</MenuItem>
						<MenuItem value="hide">Hide</MenuItem>
					</Select>
				</FormControl>
				<Button
					variant={sort === 'timestamp' ? 'contained' : 'outlined'}
					size="small"
					onClick={() => set_sort('timestamp')}
				>
					By Added
				</Button>
				<Button
					variant={sort === 'info.timestamp' ? 'contained' : 'outlined'}
					size="small"
					onClick={() => set_sort('info.timestamp')}
				>
					By Posted
				</Button>
				<Button
					variant="outlined"
					size="small"
					onClick={() => set_order(order === 'desc' ? 'asc' : 'desc')}
				>
					Sort: {order === 'desc' ? 'Newest first' : 'Oldest first'}
				</Button>
			</Box>
			<MainCard content={false}>
				<TableContainer>
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell sx={{ width: 72 }}>Thumb</TableCell>
								<TableCell>Caption / Owner</TableCell>
								<TableCell>Type</TableCell>
								<TableCell>Added</TableCell>
								<TableCell>Posted</TableCell>
								<TableCell align="center">Stopwords</TableCell>
								<TableCell align="center">Approved</TableCell>
								<TableCell align="center">Show</TableCell>
								<TableCell align="center">Preview</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{loading ? (
								<TableRow>
									<TableCell colSpan={8}>
										<Typography color="textSecondary">Loading...</Typography>
									</TableCell>
								</TableRow>
							) : items.length === 0 ? (
								<TableRow>
									<TableCell colSpan={8}>
										<Typography color="textSecondary">No entries</Typography>
									</TableCell>
								</TableRow>
							) : (
								items.map((item) => (
									<TableRow key={item._id?.toString() || item.info?.id} hover>
										<TableCell>
											{thumb_url(item) ? (
												<img
													src={thumb_url(item)}
													alt=""
													style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4 }}
												/>
											) : (
												<Box sx={{ width: 56, height: 40, bgcolor: 'grey.200' }} />
											)}
										</TableCell>
										<TableCell>
											<Typography variant="body2" noWrap sx={{ maxWidth: 320 }}>
												{((c) => (c.length > 80 ? c.slice(0, 80) + '...' : c))(
													item.info?.caption || ''
												)}
											</Typography>
											<Typography variant="caption" color="textSecondary">
												@{item.info?.ownerUsername || ''}
											</Typography>
										</TableCell>
										<TableCell>{item.info?.type === 'Video' ? 'Video' : 'Image'}</TableCell>
										<TableCell>
											{item.timestamp != null && !isNaN(item.timestamp)
												? new Date(item.timestamp).toLocaleString()
												: '-'}
										</TableCell>
										<TableCell>
											{(() => {
												const t = item.info?.timestamp;
												if (t == null) return '-';
												const d = new Date(t);
												return isNaN(d.getTime()) ? '-' : d.toLocaleString();
											})()}
										</TableCell>
										<TableCell align="center">
											{item.stopwords !== undefined ? (
												<Typography
													variant="body2"
													component="span"
													sx={{ color: item.stopwords ? 'error.main' : 'success.main', fontWeight: 500 }}
												>
													{item.stopwords ? 'Yes' : 'No'}
												</Typography>
											) : (
												'-'
											)}
										</TableCell>
										<TableCell align="center">
											<Switch
												checked={!!item.approved}
												onChange={() => handle_toggle(item, 'approved')}
											/>
										</TableCell>
										<TableCell align="center">
											<Switch
												checked={!!item.show}
												onChange={() => handle_toggle(item, 'show')}
											/>
										</TableCell>
										<TableCell align="center">
											<IconButton
												size="small"
												onClick={() => set_preview_item(item)}
												aria-label="Preview"
											>
												<VisibilityIcon fontSize="small" />
											</IconButton>
										</TableCell>
									</TableRow>
								))
							)}
						</TableBody>
					</Table>
				</TableContainer>
				<TablePagination
					component="div"
					count={total}
					page={page}
					onPageChange={(_, p) => set_page(p)}
					rowsPerPage={limit}
					onRowsPerPageChange={(e) => {
						set_limit(parseInt(e.target.value, 10));
						set_page(0);
					}}
					rowsPerPageOptions={[10, 20, 50]}
				/>
			</MainCard>
			<Dialog open={!!preview_item} onClose={() => set_preview_item(null)} maxWidth="sm" fullWidth>
				<DialogTitle sx={{ position: 'relative' }}>
					Preview
					<IconButton
						aria-label="Close"
						onClick={() => set_preview_item(null)}
						sx={{ position: 'absolute', right: 8, top: 8 }}
					>
						<CloseIcon />
					</IconButton>
				</DialogTitle>
				<DialogContent>
					{preview_item && (
						<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
							{resolve_url(preview_item.media ?? '') ? (
								/\.mp4$/i.test(preview_item.media) ? (
									<video
										src={resolve_url(preview_item.media)}
										controls
										autoPlay
										loop
										playsInline
										style={{ maxWidth: '100%', borderRadius: 4, objectFit: 'cover' }}
									/>
								) : (
									<img
										src={resolve_url(preview_item.media)}
										alt=""
										style={{ maxWidth: '100%', borderRadius: 4, objectFit: 'cover' }}
									/>
								)
							) : null}
							<Typography variant="body2" color="textSecondary">
								@{preview_item.info?.ownerUsername || ''}
							</Typography>
							<Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
								{preview_item.info?.caption || ''}
							</Typography>
						</Box>
					)}
				</DialogContent>
			</Dialog>
		</>
	);
}
